import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Field,
  Input,
  MoreTimeIcon,
  Text,
} from '@billda/design-system';
import { usePermission, Permissions } from '@billda/permissions-ui';
import {
  useOrganisationSettings,
  useSetSubmitDeadlineExtension,
} from '../hooks/useSubmitDeadlineExtension';

const MIN_DAYS = 1;
const MAX_DAYS = 30;

/**
 * Late submission settings area: how long an admin's deadline extension gives
 * the pot owner to submit. Extending is repeatable, so this is the length of
 * each extension, not a total.
 */
export const LateSubmissionArea: React.FC = () => {
  const canManage = usePermission(Permissions.Organisation.Manage);
  const { data, isLoading } = useOrganisationSettings();
  const setExtension = useSetSubmitDeadlineExtension();
  const [days, setDays] = useState('');

  const savedDays = data?.submitDeadlineExtensionDays;

  useEffect(() => {
    if (savedDays !== undefined) {
      setDays(String(savedDays));
    }
  }, [savedDays]);

  const parsed = Number(days);
  const isValid =
    days !== '' &&
    Number.isInteger(parsed) &&
    parsed >= MIN_DAYS &&
    parsed <= MAX_DAYS;
  const isUnchanged = parsed === savedDays;
  const saveDisabled =
    isLoading ||
    !canManage ||
    !isValid ||
    isUnchanged ||
    setExtension.isPending;

  return (
    <div className="mb-6 rounded-xl border border-border-default bg-surface-default p-6">
      <div className="mb-4 flex items-center">
        <MoreTimeIcon
          aria-hidden="true"
          className="mr-2 size-5 text-content-accent"
        />
        <Text variant="h6">Late submission</Text>
      </div>

      <Text as="div" variant="body2" color="secondary" className="mb-6">
        When a pot misses its submit deadline, an admin can extend the deadline
        so the owner still submits it in its own pay week, rather than the work
        rolling into the current week. This sets how long each extension lasts.
        An admin can extend again if more time is needed.
      </Text>

      <Divider className="mb-6" />

      <div className="flex items-start gap-4">
        <div className="max-w-[240px]">
          <Field
            label="Days to submit"
            error={
              days !== '' && !isValid
                ? `Enter a whole number of days between ${MIN_DAYS} and ${MAX_DAYS}`
                : undefined
            }
            hint="Counted from when the extension is given, ending at the end of that day"
          >
            <Input
              type="number"
              value={days}
              disabled={isLoading || !canManage}
              onChange={e => setDays(e.target.value)}
              invalid={days !== '' && !isValid}
              min={MIN_DAYS}
              max={MAX_DAYS}
              step={1}
              data-testid="submit-deadline-extension-days"
            />
          </Field>
        </div>
        <Button
          variant="neutral"
          className="mt-7"
          disabled={saveDisabled}
          onClick={() => setExtension.mutate(parsed)}
          data-testid="save-submit-deadline-extension"
        >
          Save
        </Button>
      </div>
    </div>
  );
};
